import { useEffect } from 'react';
import type { UseFormWatch } from 'react-hook-form';
import { useDebounceCallback } from 'usehooks-ts';

/**
 * Hook for automatically saving form values when they change
 * @param watch - The watch function from react-hook-form
 * @param onSave - Callback function that receives the form values
 * @param delayMs - Debounce delay in milliseconds
 *
 * @deprecated Use the `useOnFormValuesChange` hook instead, because it uses
 * `subscribe` instead of `watch` in line with documentation, and also avoids
 * calling the callback if nothing has changed.
 */
export function useFormAutoSave<T extends Record<string, unknown>>(
    watch: UseFormWatch<T>,
    onSave: (values: T) => void,
    delayMs = 500,
): void {
    const saveDebounced = useDebounceCallback(onSave, delayMs);

    useEffect(() => {
        const { unsubscribe } = watch((values) => {
            saveDebounced.cancel();
            saveDebounced(values as T);
        });
        return () => {
            unsubscribe();
        };
    }, [delayMs]);
}
